import { type ConnectUseCase, findConnectUseCase } from './use-cases';

/**
 * Which Connect use case a DANI conversation began from, so the chat's top
 * bar can offer to run that use case again (for another brand or period)
 * instead of only a blank new chat.
 *
 * A conversation's id is made when its first message is sent, after the use
 * case's questions are answered. So finishing the questions marks the use case
 * pending (`markUseCasePending`), and that first send claims it for the new id
 * (`claimPendingUseCase`). The record lives in this browser only; a missing
 * one just means no "run again" button.
 */

const STORAGE_KEY = 'connect.conversationUseCases';
const MAX_REMEMBERED = 50;
/** A pending use case not sent within this long is dropped, so a later,
 * unrelated chat never claims it. */
const PENDING_TTL_MS = 30 * 60 * 1000;

let pending: { id: string; at: number } | null = null;

type Remembered = Array<{ conversationId: string; useCaseId: string }>;

function read(): Remembered {
    if (typeof window === 'undefined') return [];
    try {
        const parsed: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '[]');
        return Array.isArray(parsed)
            ? parsed.filter(
                  (e): e is Remembered[number] =>
                      !!e &&
                      typeof e.conversationId === 'string' &&
                      typeof e.useCaseId === 'string',
              )
            : [];
    } catch {
        return [];
    }
}

export function markUseCasePending(useCaseId: string): void {
    pending = { id: useCaseId, at: Date.now() };
}

export function clearPendingUseCase(): void {
    pending = null;
}

/** Called with a new conversation's id as its first message is sent. */
export function claimPendingUseCase(conversationId: string): void {
    const claimed = pending;
    pending = null;
    if (!claimed || Date.now() - claimed.at > PENDING_TTL_MS) return;
    const next = [
        ...read().filter((e) => e.conversationId !== conversationId),
        { conversationId, useCaseId: claimed.id },
    ].slice(-MAX_REMEMBERED);
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
        // Storage full or blocked: the chat just won't offer "run again".
    }
}

export function connectUseCaseOf(conversationId: string | null | undefined): ConnectUseCase | null {
    if (!conversationId) return null;
    const entry = read().find((e) => e.conversationId === conversationId);
    return findConnectUseCase(entry?.useCaseId);
}
